import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createEndUser as createEndUserFn,
  updateEndUser as editEndUserService,
  deleteEndUser as deleteEndUserService,
} from "../services/end-user-service";
import { ApiError } from "pages/admin-app/shared/models/response/api-errors";
import { CreateEndUserResponse } from "../models/response/create-end-user-response";
import { CreateEndUserRequest } from "../models/request/create-end-user-request";
import { EditEndUserResponse } from "../models/response/edit-end-user-response";
import { EditEndUserRequest } from "../models/request/edit-end-user-request";
import { AxiosError } from "axios";

interface Props {
  id?: number | string;
  onEditSuccess?: () => void;
  onEditError?: () => void;
  onCreateSuccess?: () => void;
  onCreateError?: () => void;
}

export interface ApiErrorResponse {
  message: string[];
  error: string;
  statusCode: number;
}

export default function useMutateEndUser(props?: Props) {
  const queryClient = useQueryClient();

  const {
    mutate: editEndUser,
    error: editEndUserError,
    isPending: editEndUserLoading,
    isSuccess: editEndUserSuccess,
  } = useMutation<
    EditEndUserResponse,
    AxiosError<ApiErrorResponse>,
    EditEndUserRequest
  >({
    mutationKey: ["endUser", props?.id, "edit"],
    mutationFn: editEndUserService,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["endUser", props?.id] });
      queryClient.invalidateQueries({ queryKey: ["endUsers"] });
      props?.onEditSuccess && props?.onEditSuccess();
    },
    onError: () => {
      props?.onEditError && props?.onEditError();
    },
  });

  const {
    mutate: deleteEndUser,
    error: deleteEndUserError,
    isPending: deleteEndUserLoading,
    reset: deleteEndUserReset,
    isSuccess: deleteEndUserSuccess,
    isError: deleteEndUserIsError,
  } = useMutation({
    mutationKey: ["endUser", props?.id, "delete"],
    mutationFn: deleteEndUserService,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["endUsers"] });
    },
  });

  const {
    mutate: createEndUser,
    error: createEndUserError,
    isPending: createEndUserLoading,
    isSuccess: createEndUserSuccess,
  } = useMutation<CreateEndUserResponse, ApiError, CreateEndUserRequest>({
    mutationKey: ["endUser", props?.id, "create"],
    mutationFn: createEndUserFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["endUsers"] });
      props?.onCreateSuccess && props?.onCreateSuccess();
    },
    onError: () => {
      props?.onCreateError && props?.onCreateError();
    },
  });

  return {
    editEndUser,
    editEndUserError,
    editEndUserLoading,
    editEndUserSuccess,

    deleteEndUser,
    deleteEndUserError,
    deleteEndUserLoading,
    deleteEndUserReset,
    deleteEndUserSuccess,
    deleteEndUserIsError,

    createEndUser,
    createEndUserError,
    createEndUserLoading,
    createEndUserSuccess,
  };
}
